import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  IconButton,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/cs';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import LockIcon from '@mui/icons-material/Lock';
import { usePublicBookingPage } from './PublicBookingPage.hooks';
import { TimeSlot } from './api';
import { useNavigate } from 'react-router-dom';
import StepContact from './components/booking/StepContact';
import StepDateTime from './components/booking/StepDateTime';
import StepServiceSelect from './components/booking/StepServiceSelect';
import BookingSuccessScreen from './components/booking/BookingSuccessScreen';
import { useIntlTranslations } from '@libs/intl/intl.hooks';
import { useFormatCurrency } from '@features/currency/currency.hooks';

dayjs.locale('cs');

const PRIMARY = '#D02964';

const SummaryBar = ({
  service,
  date,
  slot,
}: {
  service: { serviceName: string; publicName?: string | null } | null;
  date: Dayjs | null;
  slot: TimeSlot | null;
}) => {
  if (!service) return null;
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: '12px',
        bgcolor: `${PRIMARY}10`,
        border: `1px solid ${PRIMARY}30`,
        mb: 2,
      }}
    >
      <Stack direction="row" spacing={2} flexWrap="wrap" rowGap={0.5}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <PersonIcon sx={{ fontSize: 15, color: PRIMARY }} />
          <Typography variant="caption" fontWeight={700} color={PRIMARY}>
            {service.publicName || service.serviceName}
          </Typography>
        </Stack>
        {date && (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <CalendarMonthIcon sx={{ fontSize: 15, color: PRIMARY }} />
            <Typography variant="caption" fontWeight={600} color="text.secondary">
              {date.format('D. M. YYYY')}
            </Typography>
          </Stack>
        )}
        {slot && (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <AccessTimeIcon sx={{ fontSize: 15, color: PRIMARY }} />
            <Typography variant="caption" fontWeight={600} color="text.secondary">
              {slot.start}
            </Typography>
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

const PublicBookingPage = () => {
  const navigate = useNavigate();
  const { t } = useIntlTranslations('publicBookingPage');
  const {
    isAuthForProfile,
    authLoading,
    step,
    setStep,
    profile,
    loadingProfile,
    profileError,
    selectedService,
    selectedDate,
    selectedSlot,
    slots,
    slotsReason,
    loadingSlots,
    loadingHighlightedDays,
    highlightedDays,
    currentMonth,
    contact,
    setContact,
    photos,
    maxPhotos,
    handleAddPhoto,
    handleRemovePhoto,
    submitting,
    submitError,
    submitted,
    services,
    handleSelectService,
    handleSelectSlot,
    handleDateChange,
    handleMonthChange,
    canProceedToContact,
    handleSubmit,
    isContactValid,
  } = usePublicBookingPage();
  const preferredCurrency = profile?.preferredCurrency;
  const { format: formatCurrency } = useFormatCurrency(preferredCurrency);

  const STEPS = [
    t('stepService'),
    t('stepDateTime'),
    isAuthForProfile ? 'Potvrzení' : t('stepContact'),
  ];

  if (loadingProfile || authLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100dvh">
        <CircularProgress sx={{ color: PRIMARY }} />
      </Box>
    );
  }

  if (profileError || !profile) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Alert severity="error">{profileError ?? t('profileNotFound')}</Alert>
      </Container>
    );
  }

  if (submitted) {
    return <BookingSuccessScreen />;
  }

  return (
    <Box sx={{ minHeight: '100dvh', background: 'linear-gradient(160deg, #fff0f5 0%, #fff 60%)' }}>
      {/* Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${PRIMARY} 0%, #e33f5c 100%)`,
          pt: 2,
          pb: 2.5,
          px: 2,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
          <IconButton
            size="small"
            sx={{ color: 'rgba(255,255,255,0.85)' }}
            onClick={() => {
              if (step === 0) {
                navigate('..', { relative: 'path' });
              } else {
                setStep(step - 1);
              }
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Stack direction="row" alignItems="center" spacing={1} flex={1}>
            <Typography variant="body1" fontWeight={700} sx={{ color: '#fff' }}>
              {profile.name}
            </Typography>
            {isAuthForProfile && (
              <Chip
                icon={<LockIcon sx={{ fontSize: 12, color: 'rgba(255,255,255,0.9) !important' }} />}
                label={t('regularCustomer')}
                size="small"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: '#fff',
                  fontSize: '0.65rem',
                  height: 20,
                }}
              />
            )}
          </Stack>
        </Stack>

        <Stepper
          activeStep={step}
          sx={{
            '& .MuiStepLabel-label': { color: 'rgba(255,255,255,0.6)', fontSize: '0.72rem' },
            '& .MuiStepLabel-label.Mui-active': { color: '#fff', fontWeight: 700 },
            '& .MuiStepLabel-label.Mui-completed': { color: 'rgba(255,255,255,0.85)' },
            '& .MuiStepIcon-root': { color: 'rgba(255,255,255,0.3)' },
            '& .MuiStepIcon-root.Mui-active': { color: '#fff' },
            '& .MuiStepIcon-text': { fill: '#570016', fontSize: '0.65rem', fontWeight: 700 },
            '& .MuiStepIcon-root.Mui-completed': { color: 'rgba(255,255,255,0.85)' },
            '& .MuiStepConnector-line': { borderColor: 'rgba(255,255,255,0.3)' },
          }}
        >
          {STEPS.map(label => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <Container maxWidth="sm" sx={{ pt: 3, pb: 5 }}>
        <SummaryBar service={selectedService} date={selectedDate} slot={selectedSlot} />

        {step === 0 && (
          <StepServiceSelect
            services={services}
            selectedService={selectedService}
            onSelect={handleSelectService}
            preferredCurrency={preferredCurrency ?? 'CZK'}
          />
        )}

        {step === 1 && (
          <>
            <StepDateTime
              currentMonth={currentMonth}
              selectedDate={selectedDate}
              selectedSlot={selectedSlot}
              slots={slots}
              slotsReason={slotsReason}
              loadingSlots={loadingSlots}
              loadingHighlightedDays={loadingHighlightedDays}
              highlightedDays={highlightedDays}
              onDateChange={handleDateChange}
              onMonthChange={handleMonthChange}
              onSlotClick={handleSelectSlot}
            />
            {canProceedToContact && (
              <Button
                variant="contained"
                fullWidth
                size="large"
                sx={{
                  mt: 3,
                  bgcolor: PRIMARY,
                  borderRadius: '14px',
                  py: 1.7,
                  fontWeight: 700,
                  '&:hover': { bgcolor: '#b52358', color: '#fff' },
                }}
                onClick={() => setStep(2)}
              >
                {t('continue')}
              </Button>
            )}
          </>
        )}

        {step === 2 && isAuthForProfile && (
          <>
            {/* Authenticated: show summary and confirm */}
            <Box
              sx={{
                p: 2,
                borderRadius: '12px',
                bgcolor: '#fff',
                border: '1px solid',
                borderColor: 'divider',
                mb: 2.5,
              }}
            >
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                {t('summaryTitle')}
              </Typography>
              <Stack spacing={0.75}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    {t('summaryService')}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {selectedService?.publicName || selectedService?.serviceName}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    {t('summaryDate')}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {selectedDate?.format('D. M. YYYY')}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    {t('summaryTime')}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {selectedSlot?.start} – {selectedSlot?.end}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    {t('summaryPrice')}
                  </Typography>
                  <Typography variant="body2" fontWeight={600} color={PRIMARY}>
                    {formatCurrency(selectedService?.basePrice, undefined, 0, 0)}
                  </Typography>
                </Stack>
              </Stack>
            </Box>

            <Alert severity="info" sx={{ mb: 2, borderRadius: '12px' }}>
              {t('directBookingNotice')}
            </Alert>

            {submitError && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>
                {submitError}
              </Alert>
            )}

            <Button
              variant="contained"
              fullWidth
              size="large"
              disabled={submitting}
              onClick={handleSubmit}
              sx={{
                bgcolor: PRIMARY,
                borderRadius: '14px',
                py: 1.7,
                fontWeight: 700,
                '&:hover': { bgcolor: '#b52358', color: '#fff' },
                '&.Mui-disabled': { bgcolor: `${PRIMARY}50`, color: '#fff' },
              }}
            >
              {submitting ? (
                <CircularProgress size={22} sx={{ color: '#fff' }} />
              ) : (
                'Potvrdit rezervaci'
              )}
            </Button>
          </>
        )}

        {step === 2 && !isAuthForProfile && (
          <>
            <StepContact
              contact={contact}
              onChange={(field, value) => setContact(prev => ({ ...prev, [field]: value }))}
              photos={photos}
              maxPhotos={maxPhotos}
              onAddPhoto={handleAddPhoto}
              onRemovePhoto={handleRemovePhoto}
            />

            <Divider sx={{ my: 2.5 }} />

            <Box
              sx={{
                p: 2,
                borderRadius: '12px',
                bgcolor: '#fff',
                border: '1px solid',
                borderColor: 'divider',
                mb: 2.5,
              }}
            >
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                {t('summaryTitle')}
              </Typography>
              <Stack spacing={0.75}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    {t('summaryService')}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {selectedService?.publicName || selectedService?.serviceName}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    {t('summaryDate')}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {selectedDate?.format('D. M. YYYY')}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    {t('summaryTime')}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {selectedSlot?.start} – {selectedSlot?.end}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    {t('summaryPrice')}
                  </Typography>
                  <Typography variant="body2" fontWeight={600} color={PRIMARY}>
                    {formatCurrency(selectedService?.basePrice, undefined, 0, 0)}
                  </Typography>
                </Stack>
              </Stack>
            </Box>

            {submitError && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>
                {submitError}
              </Alert>
            )}

            <Button
              variant="contained"
              fullWidth
              size="large"
              disabled={!isContactValid || submitting}
              onClick={handleSubmit}
              sx={{
                bgcolor: PRIMARY,
                borderRadius: '14px',
                py: 1.7,
                fontWeight: 700,
                '&:hover': { bgcolor: '#b52358', color: '#fff' },
                '&.Mui-disabled': { bgcolor: `${PRIMARY}50`, color: '#fff' },
              }}
            >
              {submitting ? (
                <CircularProgress size={22} sx={{ color: '#fff' }} />
              ) : (
                t('submitButton')
              )}
            </Button>
          </>
        )}
      </Container>
    </Box>
  );
};

export default PublicBookingPage;
